import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Linkedin, Send, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const cacheBust = `?_t=${Date.now()}`;
        const response = await axios.get(`${API}/content/contact${cacheBust}`);
        setContactInfo(response.data);
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };
    fetchContent();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const phone = contactInfo?.phone || '876-381-3538';
  const email = contactInfo?.email || 'info@dbrookslaw.com';
  const address = contactInfo?.address || 'Kingston, Jamaica';
  const linkedin = contactInfo?.linkedin || 'https://jm.linkedin.com/in/devon-brooks-3096b0337';
  const officeHours = contactInfo?.office_hours || 'Monday - Friday: 9:00 AM - 5:00 PM';
  const officeHoursNote = contactInfo?.office_hours_note || 'Weekend consultations by appointment';

  return (
    <div className="p-8 md:p-12 lg:p-16">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="text-center mb-12" data-testid="contact-header">
        <p className="font-body text-xs uppercase tracking-[0.2em] text-[#C9A86C] mb-2">Get In Touch</p>
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1a1412]">
          Contact Us
        </h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Contact Information */}
        <div data-testid="contact-info">
          <h2 className="font-heading text-2xl font-semibold text-[#1a1412] mb-6">
            Let's Start a Conversation
          </h2>
          <p className="font-body text-[#5a4a3a] mb-8 leading-relaxed">
            Have a legal matter you'd like to discuss? Reach out to us through any of the methods below, 
            or fill out the contact form and we'll get back to you promptly.
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-[#C9A86C]/20 hover:border-[#C9A86C]/40 transition-colors">
              <div className="bg-[#C9A86C]/10 p-3 rounded-lg">
                <Phone className="text-[#C9A86C]" size={24} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-[#1a1412]">Phone</h3>
                <a 
                  href={`tel:${phone}`}
                  className="font-body text-[#5a4a3a] hover:text-[#C9A86C] transition-colors"
                  data-testid="contact-phone-link"
                >
                  {phone}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-[#C9A86C]/20 hover:border-[#C9A86C]/40 transition-colors">
              <div className="bg-[#C9A86C]/10 p-3 rounded-lg">
                <Mail className="text-[#C9A86C]" size={24} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-[#1a1412]">Email</h3>
                <a 
                  href={`mailto:${email}`}
                  className="font-body text-[#5a4a3a] hover:text-[#C9A86C] transition-colors"
                  data-testid="contact-email-link"
                >
                  {email}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-[#C9A86C]/20 hover:border-[#C9A86C]/40 transition-colors">
              <div className="bg-[#C9A86C]/10 p-3 rounded-lg">
                <MapPin className="text-[#C9A86C]" size={24} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-[#1a1412]">Location</h3>
                <p className="font-body text-[#5a4a3a]">{address}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-[#C9A86C]/20 hover:border-[#C9A86C]/40 transition-colors">
              <div className="bg-[#C9A86C]/10 p-3 rounded-lg">
                <Linkedin className="text-[#C9A86C]" size={24} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-[#1a1412]">LinkedIn</h3>
                <a 
                  href={linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-[#5a4a3a] hover:text-[#C9A86C] transition-colors"
                  data-testid="contact-linkedin-link"
                >
                  Connect with Devon Brooks
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-[#C9A86C]/20 hover:border-[#C9A86C]/40 transition-colors">
              <div className="bg-[#C9A86C]/10 p-3 rounded-lg">
                <Clock className="text-[#C9A86C]" size={24} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-[#1a1412]">Office Hours</h3>
                <p className="font-body text-[#5a4a3a]">{officeHours}</p>
                <p className="font-body text-[#7a7065] text-sm">{officeHoursNote}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div data-testid="contact-form-section">
          {submitted ? (
            <div className="bg-white p-10 rounded-xl border border-[#C9A86C]/20 shadow-lg text-center">
              <CheckCircle className="mx-auto text-[#C9A86C] mb-4" size={48} />
              <h3 className="font-heading text-2xl font-semibold text-[#1a1412] mb-2">
                Message Sent!
              </h3>
              <p className="font-body text-[#5a4a3a] mb-6">
                Thank you for reaching out. We'll get back to you shortly.
              </p>
              <button 
                onClick={() => setSubmitted(false)}
                className="btn-secondary"
                data-testid="send-another-btn"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <div className="bg-white p-8 rounded-xl border border-[#C9A86C]/20 shadow-lg">
              <h2 className="font-heading text-2xl font-semibold text-[#1a1412] mb-6">
                Send Us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="font-body text-xs uppercase tracking-wider text-[#7a7065] mb-3 block font-medium">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input rounded-lg"
                    placeholder="Enter your full name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="font-body text-xs uppercase tracking-wider text-[#7a7065] mb-3 block font-medium">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input rounded-lg"
                    placeholder="Enter your email"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="font-body text-xs uppercase tracking-wider text-[#7a7065] mb-3 block font-medium">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input rounded-lg"
                    placeholder="Enter your phone number"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="font-body text-xs uppercase tracking-wider text-[#7a7065] mb-3 block font-medium">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="form-input rounded-lg resize-none"
                    placeholder="How can we help you?"
                    data-testid="contact-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-3 py-4 rounded-lg"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
